import { Input, Tooltip } from "antd";
import { join, slice } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { AiFillDelete } from "react-icons/ai";
import { CustomAvatar } from "../../../components/avatar/CustomAvatar";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";

const AddMemberList = ({
  title,
  onClick,
  memberGroupList,
  orgList,
  setOrgList,
}) => {
  const { user, languageMap } = useInfoUser();
  const [listUser, setListUser] = useState(
    memberGroupList?.map((e) => e[0]?.objectId) || [],
  );
  const { keydownMenuOn, setKeydownMenuOn } = useMenuContext();
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const itemRefs = useRef([]);
  const userListScrollRef = useRef(null);
  const lastUserRef = useRef(null);
  const dropdownRef = useRef(null);

  const searchOptions = useMemo(() => {
    const uniqueMap = new Map();

    orgList?.forEach((item) => {
      if (!uniqueMap.has(item?.objectId)) {
        uniqueMap.set(item?.objectId, item);
      }
    });

    return Array.from(uniqueMap.values())
      .filter(
        (item) =>
          item?.objectId !== user?.userId &&
          item?.type === "PERSON" &&
          !listUser?.includes(item?.objectId) &&
          item?.name?.toLowerCase()?.includes(searchText?.toLowerCase()),
      )
      .map((item) => ({
        label: item?.name,
        value: item?.objectId,
        avatar: item?.avatar,
        ...item,
      }));
  }, [orgList, listUser, searchText]);

  const handleChange = (userId) => {
    setListUser((prev = []) => {
      const newList = prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [userId, ...prev];

      if (!orgList?.length) {
        return newList;
      }

      const now = Date.now();

      const updatedList = orgList.map((item) => {
        if (item?.type === "INSTITUTION") {
          const children = orgList.filter(
            (child) =>
              child?.parentId === item?.objectId && child?.type === "PERSON",
          );
          const allChildrenChecked =
            children.length > 0 &&
            children.every((child) => newList.includes(child?.objectId));

          if (allChildrenChecked) {
            const latestChildTime = Math.max(
              ...children.map((c) => c.checkedTime || 0),
            );
            return {
              ...item,
              checked: true,
              checkedTime: latestChildTime || now,
            };
          } else {
            return {
              ...item,
              checked: false,
              checkedTime: null,
            };
          }
        }

        // PERSON
        const isChecked = newList.includes(item?.objectId);
        const alreadyChecked = item.checked;
        return {
          ...item,
          checked: isChecked,
          checkedTime: isChecked
            ? alreadyChecked
              ? item.checkedTime
              : now
            : null,
        };
      });

      setOrgList(updatedList);
      return newList;
    });
  };

  const handleAdd = (user) => {
    handleChange(user?.value);
    setSearchText("");
    setShowDropdown(false);
  };

  const getUserInfo = (id) =>
    orgList?.find((item) => item?.objectId === id) || { name: id };

  const generatePath = (member) => {
    const pathName = join(
      slice(member?.paths, 0, member?.paths?.length)?.map(
        (paths) => paths?.name,
      ),
      ">",
    );

    return (
      <div key={`${member?.userId}_${pathName}`} className="flex items-center">
        <div className="text-[12px]">
          <span className="text-[#d33b3d] p-[2px]">*</span>
          {pathName}
        </div>
      </div>
    );
  };

  const removeAll = () => {
    setOrgList(
      orgList?.map((item) => ({
        ...item,
        checked: false,
      })),
    );
  };

  useEffect(() => {
    if (!userListScrollRef.current) return;

    userListScrollRef.current.scrollTo({
      left: userListScrollRef.current.scrollWidth,
      behavior: "smooth",
    });
  }, [listUser]);

  useEffect(() => {
    const listUser = (memberGroupList || [])
      .map((e) => e[0])
      .filter(Boolean)
      .sort((a, b) => (a.checkedTime || 0) - (b.checkedTime || 0))
      .map((u) => u.objectId);

    setListUser(listUser);
  }, [memberGroupList]);

  useEffect(() => {
    if (itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={`flex flex-col gap-2 w-full transition-all`}>
      <div
        className="
          relative flex flex-row items-center gap-3 flex-1 
          w-full py-1 border-b border-gray-300 
          focus-within:border-blue-500 
          transition-colors duration-200 
          rounded-none
        "
      >
        <div className="flex flex-row items-center gap-3 max-w-[84%]">
          <div
            className="font-semibold text-[13px] cursor-pointer whitespace-nowrap min-w-[100px] hover:text-blue-500"
            onClick={onClick}
          >
            {title || "Thành viên"} ({listUser?.length})
          </div>

          <div
            ref={userListScrollRef}
            className="flex items-center gap-2 rounded overflow-x-auto 
            whitespace-nowrap max-w-full scroll-smooth hide-scrollbar "
          >
            {listUser?.map((id, index) => {
              const user = getUserInfo(id);
              const isLast = index === listUser?.length - 1;
              return (
                <div
                  key={id}
                  ref={isLast ? lastUserRef : null}
                  className="inline-flex items-center bg-[#e8e8e8cc] rounded-md p-1 text-sm shrink-0"
                >
                  {user?.name || id}
                  <span
                    onClick={() => handleChange(id)}
                    className="ml-1 text-xs text-gray-500 hover:text-red-500 cursor-pointer"
                  >
                    ✕
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1">
          <Input
            className="p-1 w-full"
            placeholder={
              languageMap?.["mail.search.user.placeholder"] ?? "Tìm người..."
            }
            bordered={false}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setShowDropdown(true);
              setHighlightedIndex(0);
            }}
            allowClear
            onFocus={() => {
              setShowDropdown(true);
              setKeydownMenuOn(false);
            }}
            onBlur={() => setKeydownMenuOn(true)}
            onKeyDown={(e) => {
              if (!showDropdown || searchOptions?.length === 0) return;

              if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightedIndex(
                  (prev) => (prev + 1) % searchOptions?.length,
                );
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightedIndex((prev) =>
                  prev === 0 ? searchOptions?.length - 1 : prev - 1,
                );
              } else if (e.key === "Enter") {
                e.preventDefault();
                showDropdown &&
                  searchText &&
                  handleAdd(searchOptions[highlightedIndex]);
              }
            }}
          />
        </div>
        {listUser?.length > 0 && (
          <Tooltip
            placement="top"
            title={languageMap?.["filePreview.removeAll"] ?? "Remove all"}
          >
            <button onClick={removeAll} className="mr-[20px]">
              <AiFillDelete size={20} color="red" />
            </button>
          </Tooltip>
        )}

        {showDropdown && searchText && (
          <div
            ref={dropdownRef}
            className="absolute top-[115%] z-10 w-full bg-white border rounded-md shadow-md max-h-48 overflow-y-auto"
          >
            {searchOptions?.length === 0 ? (
              <div className="px-3 py-2 text-gray-400">
                {languageMap?.["mail.search.user.notFound"] ?? "Không tìm thấy"}
              </div>
            ) : (
              searchOptions?.map((user, index) => (
                <div
                  key={user?.value}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={`justify-between px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-blue-100 ${
                    index === highlightedIndex ? "bg-blue-100" : ""
                  }`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleAdd(user)}
                >
                  <div className="flex items-center gap-2">
                    {
                      <CustomAvatar
                        person={user}
                        userId={user?.userId}
                        pos={-1}
                        styleCss={{}}
                        isShowUserStatus={{ status: true, isAlwaysTrue: false }}
                      />
                    }
                    <span>{user?.label}</span>
                  </div>
                  {generatePath(user)}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddMemberList;
